import { useMemo, useState } from "react";
import { Select } from "antd";
import { useGetProductsQuery } from "graphql/queries/get-products.generated";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import DeviceTag from "components/device-tag/device-tag";
import { SelectedProduct } from "routes/guide-details/guide-details";

import { useSearchParams } from "react-router-dom";

interface DeviceOption {
	value: string;
	label: string;
	image?: string;
}

interface DeviceSelectorProps {
	selectedDevices: SelectedProduct[];
	onDeviceSelect: (values: SelectedProduct[]) => void;
	maxDevices?: number;
	isUserEditing?: boolean;
}

const DeviceSelector = ({
	selectedDevices,
	onDeviceSelect,
	maxDevices = 5,
	isUserEditing = false
}: DeviceSelectorProps) => {
	const { loggedUserValue } = useLoggedUser();
	const [dropdownOpen, setDropdownOpen] = useState(false);

	const [searchParams] = useSearchParams();
	const handoverId = searchParams.get("handoverId") ?? undefined;

	const productsFilter = useMemo(() => {
		// should be any product that has at least one related installed_product at any property that the user can access
		const whereClause: any = {
			workspace_id: { _eq: loggedUserValue.workspace_id },
			deleted_at: { _is_null: true }
		};
		if (handoverId) {
			whereClause.handover_id = { _eq: handoverId };
			return whereClause;
		} else {
			const propertyIds = loggedUserValue.user_properties.map(element => element.property_id);

			if (propertyIds.length) {
				whereClause.installed_products = { property_id: { _in: propertyIds } };
			}

			return whereClause;
		}
	}, [loggedUserValue.user_properties, loggedUserValue.workspace_id, handoverId]);

	// Fetch devices from GraphQL
	const { data: devicesData, loading: devicesLoading } = useGetProductsQuery({
		variables: {
			where: productsFilter
		},
		errorPolicy: "all",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true
	});

	// Transform devices data into device options
	const deviceOptions: DeviceOption[] = useMemo(
		() =>
			devicesData?.products?.map(product => ({
				value: product.id,
				label: product.name,
				image: product.photo_link || `${process.env.PUBLIC_URL}/assets/images/icons/sidebar-products.svg`
			})) || [],
		[devicesData]
	);

	// Handle device selection change
	const handleDeviceChange = (selectedIds: string[]) => {
		// Limit selection to maxDevices
		if (selectedIds.length <= maxDevices) {
			const selectedProducts = selectedIds
				.map(id => devicesData?.products?.find(product => product.id === id))
				.filter(Boolean) as unknown as SelectedProduct[];

			onDeviceSelect(selectedProducts);

			// Close dropdown after selection
			setDropdownOpen(false);
		}
	};

	// Remove a selected device tag
	const handleTagClose = (deviceId: string) => {
		const newSelectedDevices = selectedDevices.filter(device => device.id !== deviceId);
		onDeviceSelect(newSelectedDevices);
	};

	return (
		<div>
			{!isUserEditing && (
				<div className="device-selector-section-title">Second, select which devices will be included in the Guide.</div>
			)}
			<label className="device-selector-label" htmlFor="device-select">
				Select up to {maxDevices} devices.
			</label>
			<Select
				id="device-select"
				className="device-selector-select"
				mode="multiple"
				placeholder="Search for device or choose from list"
				value={selectedDevices.map(device => device.id)}
				onChange={handleDeviceChange}
				open={dropdownOpen}
				onDropdownVisibleChange={setDropdownOpen}
				popupClassName="device-selector-dropdown"
				options={deviceOptions}
				showSearch
				loading={devicesLoading}
				filterOption={(input, option) => ((option?.label as string) || "")?.toLowerCase().includes(input.toLowerCase())}
				maxTagCount={0} // Don't show tags in the select itself
				optionRender={option => (
					<>
						<div className="device-selector-option-container">
							<img
								src={option.data.image}
								className="device-selector-option-image"
								onError={e => {
									(e.target as HTMLImageElement).style.display = "none";
								}}
							/>
							<span className="device-selector-option-label">{option.label}</span>
						</div>
					</>
				)}
			/>

			<div
				className={
					isUserEditing
						? "device-selector-selected-devices-container-edit"
						: "device-selector-selected-devices-container"
				}
			>
				<span>Devices:</span>{" "}
				{selectedDevices.length > 0 && (
					<div className="device-selector-tags-container">
						{selectedDevices.map(device => (
							<DeviceTag key={device.id} device={device} handleTagClose={handleTagClose} />
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default DeviceSelector;
